import React, { useEffect, useState } from "react";
import { Alert, Badge, Button, Col, Form, InputGroup, Modal, Row, Spinner, Table } from "react-bootstrap";
import IsoDatePicker from "../../../components/IsoDatePicker";
import { formatDateDMY } from "../../../utils/dateFormat";

// Editing and deleting payments already recorded on a booking - shared by the customer, transport,
// hotel and misc vendor payment sections. A delete never erases: the payment drops out of every
// total and is listed under "Show deleted" with its reason. Every change needs a reason, kept in
// the payment's change log.

export const VENDOR_PAYMENT_METHODS = [
  ["UPI", "UPI"], ["BANK_TRANSFER", "Bank Transfer"], ["CASH", "Cash"], ["OTHER", "Other"],
];
export const CUSTOMER_PAYMENT_METHODS = [
  ["UPI", "UPI"], ["BANK_TRANSFER", "Bank Transfer"], ["CASH", "Cash"], ["CARD", "Card"], ["OTHER", "Other"],
];

const rupees = (value) => `₹${Number(value || 0).toLocaleString("en-IN")}`;

const formatInstant = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });
};

/** Edit and Delete buttons for one payment row. */
export function PaymentRowActions({ payment, corrections, disabled = false }) {
  return (
    <div className="d-flex gap-1 flex-nowrap">
      <Button size="sm" variant="outline-primary" className="py-0 px-2" disabled={disabled}
        title="Edit this payment" onClick={() => corrections.openEdit(payment)}>
        <i className="bi bi-pencil" aria-hidden="true" /><span className="visually-hidden">Edit</span>
      </Button>
      <Button size="sm" variant="outline-danger" className="py-0 px-2" disabled={disabled}
        title="Delete this payment" onClick={() => corrections.openDelete(payment)}>
        <i className="bi bi-trash" aria-hidden="true" /><span className="visually-hidden">Delete</span>
      </Button>
    </div>
  );
}

/** A small "edited" tag; hovering shows every change made, with the reasons. */
export function EditedMark({ payment }) {
  if (!payment?.editedAt) return null;
  return (
    <Badge bg="light" text="dark" className="border ms-1 fw-normal" title={payment.changeLog || "Edited"}>
      edited
    </Badge>
  );
}

/** "Show deleted (n)" and the payments deleted from this section, with their reasons. */
export function DeletedPayments({ payments = [], showVendor = false }) {
  const [open, setOpen] = useState(false);
  if (!payments.length) return null;
  return (
    <div className="mt-2">
      <Button size="sm" variant="link" className="p-0 payment-deleted-toggle" onClick={() => setOpen((value) => !value)}>
        <i className={`bi ${open ? "bi-chevron-down" : "bi-chevron-right"} me-1`} aria-hidden="true" />
        {open ? "Hide" : "Show"} deleted ({payments.length})
      </Button>
      {open && (
        <div className="table-responsive mt-2">
          <Table bordered size="sm" className="mb-0 payment-deleted-table">
            <thead>
              <tr>
                <th>Date</th>{showVendor && <th>Vendor</th>}<th>Amount</th><th>Method</th><th>Reason</th><th>Deleted on</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((item) => (
                <tr key={item.id} title={item.changeLog || ""}>
                  <td>{formatDateDMY(item.paymentDate)}</td>
                  {showVendor && <td>{item.vendorName}</td>}
                  <td><s>{rupees(item.amount)}</s></td>
                  <td>{item.paymentMethod?.replaceAll("_", " ")}</td>
                  <td>{item.deleteReason || "-"}</td>
                  <td>{formatInstant(item.deletedAt)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
}

const formFrom = (payment) => ({
  amount: payment?.amount != null ? String(payment.amount) : "",
  paymentDate: payment?.paymentDate || "",
  paymentMethod: payment?.paymentMethod || "UPI",
  referenceNumber: payment?.referenceNumber || "",
  note: payment?.note || "",
  vendorName: payment?.vendorName || "",
  reason: "",
});

/**
 * State and dialogs for correcting payments in one section. `editPayment(payment, body)` and
 * `deletePayment(payment, body)` call the API and resolve to the section's new summary, which is
 * handed to `onChanged`.
 */
export function usePaymentCorrections({ methods = VENDOR_PAYMENT_METHODS, withVendor = false,
  editPayment, deletePayment, onChanged }) {
  const [target, setTarget] = useState(null); // { mode: "edit" | "delete", payment }
  const [form, setForm] = useState(formFrom(null));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (target) {
      setForm(formFrom(target.payment));
      setError("");
    }
  }, [target]);

  const close = () => !saving && setTarget(null);
  const patch = (field, value) => setForm((old) => ({ ...old, [field]: value }));
  const reasonOk = form.reason.trim().length >= 3;

  const submit = async () => {
    if (!target || !reasonOk) return;
    setSaving(true);
    setError("");
    try {
      const summary = target.mode === "edit"
        ? await editPayment(target.payment, {
          amount: Number(form.amount || 0),
          paymentDate: form.paymentDate || null,
          paymentMethod: form.paymentMethod,
          referenceNumber: form.referenceNumber,
          note: form.note,
          vendorName: withVendor ? form.vendorName : null,
          reason: form.reason,
        })
        : await deletePayment(target.payment, { reason: form.reason });
      onChanged?.(summary);
      setTarget(null);
    } catch (err) {
      setError(err?.response?.data?.message || (target.mode === "edit" ? "Could not save the change." : "Could not delete the payment."));
    } finally {
      setSaving(false);
    }
  };

  const payment = target?.payment;
  const editing = target?.mode === "edit";
  const editReady = Number(form.amount) > 0 && form.note.trim() && (!withVendor || form.vendorName.trim());

  const modal = (
    <Modal show={!!target} onHide={close} centered>
      <Modal.Header closeButton={!saving}>
        <Modal.Title>{editing ? "Edit payment" : "Delete payment?"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {payment && !editing && (
          <div className="mb-3">
            <div className="fw-semibold">{rupees(payment.amount)} · {payment.paymentMethod?.replaceAll("_", " ")}</div>
            <div className="small">
              {formatDateDMY(payment.paymentDate)}
              {payment.vendorName ? ` · ${payment.vendorName}` : ""}
              {payment.note ? ` · ${payment.note}` : ""}
            </div>
            <Alert variant="warning" className="small mt-3 mb-0">
              This payment will be removed from all totals and balances. It stays on record under
              "Show deleted" with your reason.
            </Alert>
          </div>
        )}
        {payment && editing && (
          <Row className="g-2 mb-3">
            {withVendor && (
              <Col xs={12}><Form.Label>Vendor *</Form.Label>
                <Form.Control value={form.vendorName} onChange={(e) => patch("vendorName", e.target.value)} /></Col>
            )}
            <Col md={6}><Form.Label>Amount *</Form.Label>
              <InputGroup><InputGroup.Text>₹</InputGroup.Text>
                <Form.Control type="number" min="0.01" step="0.01" value={form.amount} onChange={(e) => patch("amount", e.target.value)} />
              </InputGroup></Col>
            <Col md={6}><Form.Label>Payment Date *</Form.Label>
              <IsoDatePicker value={form.paymentDate} onChange={(value) => patch("paymentDate", value)} /></Col>
            <Col md={6}><Form.Label>Method *</Form.Label>
              <Form.Select value={form.paymentMethod} onChange={(e) => patch("paymentMethod", e.target.value)}>
                {methods.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </Form.Select></Col>
            <Col md={6}><Form.Label>Reference / UTR</Form.Label>
              <Form.Control value={form.referenceNumber} onChange={(e) => patch("referenceNumber", e.target.value)} /></Col>
            <Col xs={12}><Form.Label>Payment Note *</Form.Label>
              <Form.Control value={form.note} onChange={(e) => patch("note", e.target.value)} /></Col>
          </Row>
        )}
        <Form.Group>
          <Form.Label className="fw-semibold">Reason for {editing ? "this change" : "deleting"} *</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            maxLength={255}
            value={form.reason}
            placeholder={editing ? "e.g. Wrong amount entered" : "e.g. UPI transaction failed"}
            onChange={(e) => patch("reason", e.target.value)}
          />
        </Form.Group>
        {error && <Alert variant="danger" className="small mt-3 mb-0">{error}</Alert>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" disabled={saving} onClick={close}>Cancel</Button>
        <Button
          variant={editing ? "primary" : "danger"}
          disabled={saving || !reasonOk || (editing && !editReady)}
          onClick={submit}
        >
          {saving ? <Spinner animation="border" size="sm" /> : editing ? "Save change" : "Delete payment"}
        </Button>
      </Modal.Footer>
    </Modal>
  );

  return {
    openEdit: (item) => setTarget({ mode: "edit", payment: item }),
    openDelete: (item) => setTarget({ mode: "delete", payment: item }),
    modal,
  };
}
